import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { merge, Observable, of, Subject, throwError } from 'rxjs';

import { TUser } from 'src/app/project/modules/user/user.model';
import { EIntegrationStatus, TAllCustomFields, TCustomField } from '../custom-fields.model';

import { TWorkspace, TWorkspacesById } from 'src/app/project/modules/workspace/workspace.model';

import { AccountService } from 'src/app/project/modules/account/account-service/account.service';
import { UserService } from 'src/app/project/modules/user/user.service';
import { WorkspaceService } from 'src/app/project/modules/workspace/workspace.service';
import { ActiveService } from 'src/app/project/services/active/active.service';
import { Modal, ModalService } from '../../../components/modal/modal.service';
import { PromptService } from '../../../components/prompt/prompt.service';
import { CustomFieldsService, ECustomFieldsEventType } from '../custom-fields.service';

import { Router } from '@angular/router';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { TNewCustomField } from 'src/app/project/data-providers/api-providers/workspace-api-provider/workspace-requests.model';
import { translate } from 'src/app/project/features/translate/translate';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { getErrorMessage } from 'src/app/project/helpers/database/get-error-message';
import { EUserRole } from 'src/app/project/modules/share/share-utils/user-roles';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { EInstance } from 'src/app/project/shared/enums/instances';
import { generateErrorPrompt } from '../../errors/response-error';
import { EUserType } from '../../users/user-types-enum';
import { ECustomFieldType } from '../custom-field-types-enums';
import { getIsContainsSlash } from '../custom-fields-service.utils';
import { customFieldTypesData, TCustomFieldTypeData } from '../custom-fields.data';
import { TAddCustomFieldModalData, TSelectedCustomFields } from './add-custom-field.model';
import { AddCustomFieldService } from './add-custom-field.service';
import { ImportCustomFieldsService } from './import-custom-fields.service';
import { countSelectedFields } from './utils/count-selected-fields';
import {
  getImportCustomFieldError,
  parseImportCustomFieldError,
} from './utils/get-import-custom-field-error';
import { getIsBasicFieldName } from './utils/get-is-basic-field-name';
import { EImportCustomFieldError } from './utils/import-custom-fields.consts';
import {
  TGroupOption,
  TLayoutConfig,
} from 'react-lib/components/organisms/CustomFieldGroupsSettings';
import type { ImportCustomFieldGroupsSelection } from 'react-lib/components/organisms/ImportCustomFieldGroups';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { EFeatureFlag } from 'src/app/core/services/feature-flags-consts';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

const IMPORT_ERROR_STATUS_CODES: number[] = [
  EStatusCode.BAD_REQUEST,
  EStatusCode.UNPROCESSABLE_ENTITY,
];

@Component({
  selector: 'app-add-custom-field-modal',
  templateUrl: './add-custom-field-modal.component.html',
  styleUrls: ['./add-custom-field-modal.component.scss'],
  standalone: false,
})
export class AddCustomFieldModalComponent implements OnInit, OnDestroy {
  customFields: TAllCustomFields = this.customFieldsService.getCustomFields();
  workspace: TWorkspace = null;
  workspaceId: string;
  workspaces: TWorkspacesById = null;
  importingCustomFields = false;
  processingImport = false;
  selectedCustomFields: TSelectedCustomFields = {};
  step: 'details' | 'select' = 'select';
  emptyListLabel = false;
  timelineExists = false;
  timelineEnabled = false;
  focused = false;
  sorted = this.customFieldsService.getAllSorted();
  maxListDepthExceeded = false;
  EUserRole = EUserRole;
  customFieldTypes = ECustomFieldType;
  showingFormulaError = false;
  customFieldGroupOptions: TLayoutConfig[] = [];
  iconMap = iconMap;
  totalSelectedFields: {
    number: number;
  } = {
    number: 0,
  };

  customFieldGroupsImport: ImportCustomFieldGroupsSelection = null;
  formulaFieldsMissingOperands: string[] = [];
  existingGroupNames: string[] = [];
  importErrorMessage = '';
  preselectedGroupId: string;
  hasDigitalSignaturesFlagEnabled: boolean;
  customFieldTypesData: TCustomFieldTypeData[] = [];

  field: TCustomField = {
    type: null,
    label: '',
    currencyCode: null,
    subList: null,
    unit: null,
    decimalPlaces: null,
    showTotal: true,
    subValuesActive: false,
    showHoursOnly: false,
    showCommas: false,
    id: null,
    display: true,
    volyIntegrationActive: EIntegrationStatus.OFF,
    lockedTemplate: false,
    lockedValue: false,
    workspaceId: null,
    outputType: null,
    description: '',
    formula: '',
  };

  processing = false;
  listCFError = false;
  integrationsVisible: boolean;
  customFieldGroupsEnabled: boolean;

  user: TUser = {};

  private listDepth = 0;
  private maxListDepth: number;
  EIconPath = EIconPath;

  private readonly destroy$ = new Subject<void>();
  private modal: Modal<TAddCustomFieldModalData> = this.modalService.getModal();

  constructor(
    @Inject('HOSTNAME') private host: string,
    private customFieldsService: CustomFieldsService,
    private promptService: PromptService,
    private modalService: ModalService,
    private workspaceService: WorkspaceService,
    private activeService: ActiveService,
    private userService: UserService,
    private translationPipe: TranslationPipe,
    private accountService: AccountService,
    private addCustomFieldService: AddCustomFieldService,
    private importCustomFieldsService: ImportCustomFieldsService,
    private featureFlagsService: FeatureFlagService,
    private router: Router,
  ) {
    this.user = this.userService.getUser();

    this.customFieldGroupsEnabled = this.featureFlagsService.getFlag(
      EFeatureFlag.CUSTOM_FIELD_GROUPS,
    );
    this.hasDigitalSignaturesFlagEnabled = this.featureFlagsService.getFlag(
      EFeatureFlag.DIGITAL_SIGNATURES,
    );

    this.featureFlagsService.flagsChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.customFieldGroupsEnabled = this.featureFlagsService.getFlag(
        EFeatureFlag.CUSTOM_FIELD_GROUPS,
      );
      this.hasDigitalSignaturesFlagEnabled = this.featureFlagsService.getFlag(
        EFeatureFlag.DIGITAL_SIGNATURES,
      );
    });
  }

  ngOnInit(): void {
    if (this.modal.props?.customFieldGroupOptions) {
      this.customFieldGroupOptions = this.modal.props.customFieldGroupOptions as TLayoutConfig[];

      const preselectedGroup = this.modal.props?.preselectedGroup as TGroupOption;

      this.preselectedGroupId = preselectedGroup?.value;
      this.field.groupId = preselectedGroup?.value ?? '';
    }

    this.workspaceId = this.modal.data.workspaceId;

    const customFields = this.customFieldsService.getCustomFields()[this.workspaceId];

    this.workspaces = this.workspaceService.getWorkspaces();
    this.workspace = this.workspaces[this.workspaceId];
    const account = this.accountService.getAccount(this.workspace.accountId);

    if (this.modal.data) {
      this.importingCustomFields = this.modal.data.importingCustomFields;
    }

    this.timelineEnabled = account.accountFeatures.timeline;

    Object.keys(customFields).forEach((customFieldId) => {
      if (customFields[customFieldId].type === ECustomFieldType.TIMELINE) {
        this.timelineExists = true;
      }
    });

    merge(
      this.customFieldsService.depthChange$.pipe(
        tap(() => {
          this.listDepth = this.customFieldsService.getCustomFieldListDepth(
            this.field.subList || [],
          );
        }),
      ),
      this.customFieldsService.maxListDepthChange$.pipe(
        tap((depth) => {
          this.maxListDepth = depth;
        }),
      ),
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkDepthExceeded();
      });

    this.setVisibleIntegrationsField();
    this.setVisibleFields();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  navToInt(): void {
    this.router.navigate(['/settings/site/', this.workspaceId, 'integrations']);
    this.hideModal();
  }

  save(): void {
    if (!this.modal.processing && !this.processing) {
      this.processing = true;
      this.field.label = this.field.label.trim();

      const workspaces = this.workspaceService.getWorkspaces();

      const fieldIndex = workspaces[this.workspaceId].customFields.findIndex(
        (fieldId) =>
          this.customFields[this.workspaceId][fieldId].label.toLowerCase() ===
          this.field.label.toLowerCase(),
      );

      if (fieldIndex > -1 || getIsBasicFieldName(this.field.label.toLowerCase())) {
        const promptText = this.translationPipe.transform('prompt_new_custom_field_taken');

        this.promptService.showWarning(promptText);
        this.processing = false;
      } else {
        if (this.field.type === ECustomFieldType.COST) {
          if (!this.field.currencyCode) {
            const promptText = this.translationPipe.transform('prompt_select_currency');

            this.promptService.showWarning(promptText);
            this.processing = false;
            return;
          }
        }

        if (
          this.field.type !== ECustomFieldType.LIST &&
          this.field.type !== ECustomFieldType.MULTI_LIST
        ) {
          delete this.field.subList;
        } else {
          const listIsValid = this.validateList();

          if (!listIsValid) {
            this.processing = false;

            return;
          }
        }

        const newFieldData: TNewCustomField =
          this.customFieldsService.generateCustomFieldRequestData(this.field);

        this.addCustomFieldService
          .addCustomField(this.workspaceId, newFieldData)
          .pipe(
            finalize(() => {
              this.processing = false;
            }),
          )
          .subscribe();
      }
    }
  }

  uploadExcel(files: File[], type: ECustomFieldType): void {
    this.processing = true;
    const workspaceId = this.activeService.getActiveWorkspaceId();

    this.addCustomFieldService
      .addListFieldFromExcel(workspaceId, files[0], type)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) => {
          let promptText = this.translationPipe.transform('prompt_field_create_error');

          if (err.status === EStatusCode.BAD_REQUEST) {
            promptText = this.translationPipe.transform('prompt_new_custom_field_taken');
          }

          this.promptService.showError(promptText);
          return throwError(err);
        }),
        finalize(() => {
          this.processing = false;
        }),
      )
      .subscribe();
  }

  hideModal(): void {
    this.modalService.hideModal();
  }

  setType(type: ECustomFieldType): void {
    this.step = 'details';
    this.field.type = type;
  }

  updateCustomFieldGroupsSelection(selection: ImportCustomFieldGroupsSelection): void {
    this.importErrorMessage = '';
    this.customFieldGroupsImport = selection;
    this.totalSelectedFields.number = countSelectedFields(selection);
  }

  updateFormulaFieldsMissingOperands(fieldLabels: string[]): void {
    this.formulaFieldsMissingOperands = fieldLabels;
  }

  getFormulaOperandsMissingMessage(): string {
    return translate('import_custom_fields_formula_operands_missing', {
      fieldNames: this.formulaFieldsMissingOperands.join(', '),
    });
  }

  updateExistingGroupNames(groupNames: string[]): void {
    this.existingGroupNames = groupNames;
  }

  getExistingGroupsMessage(): string {
    return translate('import_custom_fields_group_exists', {
      groupNames: this.existingGroupNames.join(', '),
    });
  }

  getImportButtonText(): string {
    const selectedFields = this.totalSelectedFields.number;

    if (selectedFields === 0) {
      return this.translationPipe.transform('import_fields');
    }

    if (selectedFields === 1) {
      return this.translationPipe.transform('import_fields_one');
    }

    return this.translationPipe.transform('import_fields_other', { count: selectedFields });
  }

  checkImportButtonDisabled(): boolean {
    if (!this.customFieldGroupsEnabled) {
      return this.totalSelectedFields.number === 0;
    }

    return this.formulaFieldsMissingOperands.length > 0;
  }

  toggleImport(importing: boolean): void {
    this.customFieldGroupsImport = null;
    this.totalSelectedFields.number = 0;
    this.importErrorMessage = '';
    this.existingGroupNames = [];
    this.importingCustomFields = importing;
  }

  importCustomFields(): void {
    if (this.processingImport) {
      return;
    }

    this.processingImport = true;
    this.importErrorMessage = '';

    if (this.customFieldGroupsEnabled) {
      this.importCustomFieldGroups();

      return;
    }

    this.deprecatedImportCustomFields();
  }

  private importCustomFieldGroups(): void {
    if (this.totalSelectedFields.number === 0) {
      this.processingImport = false;
      this.importErrorMessage = this.translationPipe.transform('import_no_fields_selected');

      return;
    }

    this.importCustomFieldsService
      .importCustomFieldGroups(this.customFieldGroupsImport, this.workspaceId)
      .pipe(
        tap(() => {
          this.modalService.hideModal(false);
        }),
        catchError((error) => this.handleImportGroupsError(error)),
        finalize(() => {
          this.processingImport = false;
        }),
      )
      .subscribe();
  }

  deprecatedImportCustomFields() {
    this.importCustomFieldsService
      .importCustomFields(
        this.selectedCustomFields,
        this.workspaceId,
        this.workspace,
        this.workspaces,
      )
      .pipe(
        tap(() => {
          this.hideModal();
        }),
        catchError((error) => this.handleImportError(error)),
        finalize(() => {
          this.processingImport = false;
        }),
      )
      .subscribe();
  }

  private handleImportError(error: HttpErrorResponse): Observable<null> {
    if (error.status !== EStatusCode.BAD_REQUEST) {
      this.promptService.showError(generateErrorPrompt(error, 'prompt_error'));

      return of(null);
    }

    getErrorMessage(error).then((errorMessage: string) => {
      this.showFormulaErrorIfNeeded(errorMessage);

      this.promptService.showError(getImportCustomFieldError(errorMessage));
    });

    return of(null);
  }

  private handleImportGroupsError(error: HttpErrorResponse): Observable<null> {
    if (!IMPORT_ERROR_STATUS_CODES.includes(error.status)) {
      this.importErrorMessage = generateErrorPrompt(error, 'prompt_error');

      return of(null);
    }

    getErrorMessage(error).then((errorMessage: string) => {
      this.showFormulaErrorIfNeeded(errorMessage);

      this.importErrorMessage = getImportCustomFieldError(errorMessage);
    });

    return of(null);
  }

  private showFormulaErrorIfNeeded(errorMessage: string): void {
    const { code } = parseImportCustomFieldError(errorMessage);

    if (code === EImportCustomFieldError.CFT_IMPORT_FORMULA_ERROR) {
      this.toggleFormulaErrorModal(true);
    }
  }

  back(): void {
    this.step = 'select';
  }

  updateListError(error: boolean): void {
    this.listCFError = error;
  }

  validateList(): boolean {
    let slashFound = false;

    if (this.field.subList) {
      slashFound = getIsContainsSlash(this.field.subList);
    }

    this.emptyListLabel = JSON.stringify(this.field).indexOf('"label":""') !== -1;

    if (this.emptyListLabel) {
      const promptText = this.translationPipe.transform('prompt_fill_all_warning');

      this.promptService.showWarning(promptText);
      this.customFieldsService.emit(ECustomFieldsEventType.SAVE_CUSTOM_FIELD_CLICKED);

      return false;
    }

    if (slashFound) {
      const promptText = this.translationPipe.transform(
        'prompt_invalid_character_slash_custom_field',
      );

      this.promptService.showWarning(promptText);

      return false;
    }

    return true;
  }

  checkSaveButtonDisabled(): boolean {
    const isMissingLabel = !this.field.label;
    const isMissingFormula = this.field.type === ECustomFieldType.FORMULA && !this.field.formula;
    const isCostField = this.field.type === ECustomFieldType.COST;
    const isMissingFormulaOutput =
      this.field.type === ECustomFieldType.FORMULA && !this.field.outputType;
    const isFormulaCostOutput =
      this.field.type === ECustomFieldType.FORMULA &&
      this.field.outputType === ECustomFieldType.COST;
    const isMissingCost = (isCostField || isFormulaCostOutput) && !this.field.currencyCode;

    return (
      isMissingLabel ||
      this.processing ||
      this.listCFError ||
      isMissingCost ||
      isMissingFormulaOutput ||
      isMissingFormula
    );
  }

  private checkDepthExceeded(): void {
    this.maxListDepthExceeded = this.listDepth > this.maxListDepth;
  }

  private setVisibleIntegrationsField(): void {
    const user = this.userService.getUser();
    const isDeveloper = user.userType === EUserType.DEVELOPER || user.userType === EUserType.TESTER;
    const isASM = this.host === EInstance.ASM;

    this.integrationsVisible = isDeveloper || !isASM;
  }

  private setVisibleFields(): void {
    if (this.hasDigitalSignaturesFlagEnabled) {
      this.customFieldTypesData = customFieldTypesData;

      return;
    }

    this.customFieldTypesData = customFieldTypesData.filter(
      (customField) => customField.value !== ECustomFieldType.SIGNATURE,
    );
  }

  toggleFormulaErrorModal(showing: boolean): void {
    this.showingFormulaError = showing;
  }
}
